"use client";

import { useEffect, useState, useCallback } from "react";
import { createClient } from "@/lib/supabase/client";
import Icon from "@/components/ui/AppIcon";

// ── Types ──────────────────────────────────────────────────────────────────

interface TargetOfWeek {
  id: string;
  brand: string;
  reason: string;
  since: string;
  competitor: string | null;
  tweet_text: string | null;
  is_active: boolean;
}

interface BlacklistEntry {
  id: string;
  brand: string;
  industry: string;
  streaming_services: string[];
  ad_type: string;
  report_count: number;
  last_seen: string;
  blacklist_status: "pending" | "approved" | "rejected";
  upvotes: number;
  downvotes: number;
  notes: string | null;
  submitted_by: string | null;
  created_at: string;
}

interface CommunityReport {
  id: string;
  brand: string;
  streaming_service: string;
  industry: string;
  ad_type: string;
  notes: string | null;
  report_status: "open" | "reviewed" | "resolved" | "dismissed";
  reporter_email: string | null;
  created_at: string;
}

interface Subscriber {
  id: string;
  email: string;
  is_active: boolean;
  subscribed_at: string;
  unsubscribed_at: string | null;
}

type AdminTab = "target" | "blacklist" | "reports" | "subscribers";

// ── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function StatusPill({
  value,
  map,
}: {
  value: string;
  map: Record<string, { label: string; cls: string }>;
}) {
  const cfg = map[value] ?? { label: value, cls: "bg-gray-200 text-gray-700" };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
}

const blacklistStatusMap: Record<string, { label: string; cls: string }> = {
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-700" },
  approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700" },
};

const reportStatusMap: Record<string, { label: string; cls: string }> = {
  open: { label: "Open", cls: "bg-blue-100 text-blue-700" },
  reviewed: { label: "Reviewed", cls: "bg-amber-100 text-amber-700" },
  resolved: { label: "Resolved", cls: "bg-emerald-100 text-emerald-700" },
  dismissed: { label: "Dismissed", cls: "bg-gray-200 text-gray-600" },
};

// ── Main Component ─────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const supabase = createClient();
  const [activeTab, setActiveTab] = useState<AdminTab>("target");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);

  // ── Target of Week state ──
  const [target, setTarget] = useState<TargetOfWeek | null>(null);
  const [targetForm, setTargetForm] = useState({
    brand: "",
    reason: "",
    since: "",
    competitor: "",
    tweet_text: "",
  });
  const [editingTarget, setEditingTarget] = useState(false);

  // ── Blacklist state ──
  const [blacklist, setBlacklist] = useState<BlacklistEntry[]>([]);
  const [blacklistFilter, setBlacklistFilter] = useState<"all" | "pending" | "approved" | "rejected">("all");
  const [blacklistSearch, setBlacklistSearch] = useState("");
  const [editingBlacklist, setEditingBlacklist] = useState<BlacklistEntry | null>(null);

  // ── Reports state ──
  const [reports, setReports] = useState<CommunityReport[]>([]);
  const [reportFilter, setReportFilter] = useState<"all" | "open" | "reviewed" | "resolved" | "dismissed">("all");

  // ── Subscribers state ──
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [subscriberFilter, setSubscriberFilter] = useState<"all" | "active" | "inactive">("all");

  // ── Toast helper ──
  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Fetch functions ──
  const fetchTarget = useCallback(async () => {
    const { data } = await supabase
      .from("target_of_week")
      .select("*")
      .eq("is_active", true)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (data) {
      setTarget(data);
      setTargetForm({
        brand: data.brand,
        reason: data.reason,
        since: data.since,
        competitor: data.competitor ?? "",
        tweet_text: data.tweet_text ?? "",
      });
    }
  }, []);

  const fetchBlacklist = useCallback(async () => {
    const { data } = await supabase
      .from("advertiser_blacklist")
      .select("*")
      .order("created_at", { ascending: false });
    setBlacklist(data ?? []);
  }, []);

  const fetchReports = useCallback(async () => {
    const { data } = await supabase
      .from("community_reports")
      .select("*")
      .order("created_at", { ascending: false });
    setReports(data ?? []);
  }, []);

  const fetchSubscribers = useCallback(async () => {
    const { data } = await supabase
      .from("newsletter_subscribers")
      .select("*")
      .order("subscribed_at", { ascending: false });
    setSubscribers(data ?? []);
  }, []);

  useEffect(() => {
    fetchTarget();
    fetchBlacklist();
    fetchReports();
    fetchSubscribers();
  }, [fetchTarget, fetchBlacklist, fetchReports, fetchSubscribers]);

  // ── Target actions ──
  const handleSaveTarget = async () => {
    setLoading(true);
    try {
      if (target) {
        const { error } = await supabase
          .from("target_of_week")
          .update({ ...targetForm })
          .eq("id", target.id);
        if (error) throw error;
        showToast("Target updated successfully");
      } else {
        const { error } = await supabase
          .from("target_of_week")
          .insert({ ...targetForm, is_active: true });
        if (error) throw error;
        showToast("Target created successfully");
      }
      setEditingTarget(false);
      await fetchTarget();
    } catch {
      showToast("Failed to save target", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Blacklist actions ──
  const handleBlacklistStatus = async (id: string, status: "approved" | "rejected") => {
    const { error } = await supabase
      .from("advertiser_blacklist")
      .update({ blacklist_status: status })
      .eq("id", id);
    if (error) { showToast("Update failed", "error"); return; }
    showToast(`Entry ${status}`);
    await fetchBlacklist();
  };

  const handleDeleteBlacklist = async (id: string) => {
    if (!confirm("Delete this entry?")) return;
    const { error } = await supabase.from("advertiser_blacklist").delete().eq("id", id);
    if (error) { showToast("Delete failed", "error"); return; }
    showToast("Entry deleted");
    await fetchBlacklist();
  };

  const handleSaveBlacklist = async () => {
    if (!editingBlacklist) return;
    setLoading(true);
    const { error } = await supabase
      .from("advertiser_blacklist")
      .update({
        brand: editingBlacklist.brand,
        industry: editingBlacklist.industry,
        ad_type: editingBlacklist.ad_type,
        notes: editingBlacklist.notes,
        blacklist_status: editingBlacklist.blacklist_status,
      })
      .eq("id", editingBlacklist.id);
    setLoading(false);
    if (error) { showToast("Save failed", "error"); return; }
    showToast("Entry saved");
    setEditingBlacklist(null);
    await fetchBlacklist();
  };

  // ── Report actions ──
  const handleReportStatus = async (
    id: string,
    status: "open" | "reviewed" | "resolved" | "dismissed"
  ) => {
    const { error } = await supabase
      .from("community_reports")
      .update({ report_status: status })
      .eq("id", id);
    if (error) { showToast("Update failed", "error"); return; }
    showToast(`Report marked as ${status}`);
    await fetchReports();
  };

  const handleDeleteReport = async (id: string) => {
    if (!confirm("Delete this report?")) return;
    const { error } = await supabase.from("community_reports").delete().eq("id", id);
    if (error) { showToast("Delete failed", "error"); return; }
    showToast("Report deleted");
    await fetchReports();
  };

  // ── Subscriber actions ──
  const handleToggleSubscriber = async (id: string, currentActive: boolean) => {
    const { error } = await supabase
      .from("newsletter_subscribers")
      .update({
        is_active: !currentActive,
        unsubscribed_at: !currentActive ? null : new Date().toISOString(),
      })
      .eq("id", id);
    if (error) { showToast("Update failed", "error"); return; }
    showToast(currentActive ? "Subscriber deactivated" : "Subscriber reactivated");
    await fetchSubscribers();
  };

  const handleDeleteSubscriber = async (id: string) => {
    if (!confirm("Remove this subscriber?")) return;
    const { error } = await supabase.from("newsletter_subscribers").delete().eq("id", id);
    if (error) { showToast("Delete failed", "error"); return; }
    showToast("Subscriber removed");
    await fetchSubscribers();
  };

  // ── Filtered data ──
  const filteredBlacklist = blacklist.filter((e) => {
    const matchStatus = blacklistFilter === "all" || e.blacklist_status === blacklistFilter;
    const matchSearch =
      !blacklistSearch ||
      e.brand.toLowerCase().includes(blacklistSearch.toLowerCase()) ||
      e.industry.toLowerCase().includes(blacklistSearch.toLowerCase());
    return matchStatus && matchSearch;
  });

  const filteredReports = reports.filter(
    (r) => reportFilter === "all" || r.report_status === reportFilter
  );

  const filteredSubscribers = subscribers.filter((s) => {
    if (subscriberFilter === "active") return s.is_active;
    if (subscriberFilter === "inactive") return !s.is_active;
    return true;
  });

  // ── Stats ──
  const stats = {
    pendingBlacklist: blacklist.filter((e) => e.blacklist_status === "pending").length,
    openReports: reports.filter((r) => r.report_status === "open").length,
    activeSubscribers: subscribers.filter((s) => s.is_active).length,
    totalSubscribers: subscribers.length,
  };

  const tabs: { id: AdminTab; label: string; icon: string; badge?: number }[] = [
    { id: "target", label: "Target of Week", icon: "ExclamationTriangleIcon" },
    { id: "blacklist", label: "Blacklist", icon: "ShieldExclamationIcon", badge: stats.pendingBlacklist },
    { id: "reports", label: "Reports", icon: "FlagIcon", badge: stats.openReports },
    { id: "subscribers", label: "Subscribers", icon: "EnvelopeIcon" },
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold shadow-xl animate-fade-up ${
            toast.type === "success" ?"bg-emerald-500 text-white" :"bg-[#FF2222] text-white"
          }`}
          role="alert"
        >
          <Icon
            name={toast.type === "success" ? "CheckCircleIcon" : "XCircleIcon"}
            size={16}
            variant="solid"
          />
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="border-b border-white/10 bg-[#0D0D0D] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#FF2222] flex items-center justify-center">
              <Icon name="ShieldCheckIcon" size={16} variant="solid" className="text-white" />
            </div>
            <div>
              <h1 className="font-display text-lg font-black text-white leading-none">
                Admin Dashboard
              </h1>
              <p className="text-[11px] text-white/40 font-medium">PaidMeansPaid Control Panel</p>
            </div>
          </div>
          <a
            href="/homepage"
            className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/80 transition-colors"
          >
            <Icon name="ArrowLeftIcon" size={13} variant="outline" />
            Back to site
          </a>
        </div>
      </div>

      {/* Stats bar */}
      <div className="border-b border-white/5 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Pending Blacklist", value: stats.pendingBlacklist, color: "text-amber-400" },
            { label: "Open Reports", value: stats.openReports, color: "text-blue-400" },
            { label: "Active Subscribers", value: stats.activeSubscribers, color: "text-emerald-400" },
            { label: "Total Subscribers", value: stats.totalSubscribers, color: "text-white" },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <div className={`font-display text-2xl font-black ${s.color}`}>{s.value}</div>
              <div className="text-[11px] text-white/40 font-medium mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/10 bg-[#0D0D0D] sticky top-[65px] z-30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex items-center gap-2 px-4 py-3.5 text-sm font-semibold whitespace-nowrap transition-all border-b-2 ${
                  activeTab === tab.id
                    ? "border-[#FF2222] text-white"
                    : "border-transparent text-white/40 hover:text-white/70"
                }`}
              >
                <Icon name={tab.icon} size={15} variant={activeTab === tab.id ? "solid" : "outline"} />
                {tab.label}
                {tab.badge != null && tab.badge > 0 && (
                  <span className="ml-1 bg-[#FF2222] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* ── TARGET OF WEEK ── */}
        {activeTab === "target" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-black text-white">Target of the Week</h2>
              {!editingTarget && (
                <button
                  onClick={() => setEditingTarget(true)}
                  className="flex items-center gap-2 bg-[#FF2222] text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-[#CC0000] transition-colors"
                >
                  <Icon name="PencilSquareIcon" size={14} variant="solid" />
                  {target ? "Edit Target" : "Set Target"}
                </button>
              )}
            </div>

            {!editingTarget && target ? (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#FF2222] flex items-center justify-center flex-shrink-0 blink-alert">
                    <Icon name="ExclamationTriangleIcon" size={18} variant="solid" className="text-white" />
                  </div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-widest text-[#FF2222] block mb-1">
                      🚨 Current Target
                    </span>
                    <h3 className="font-display text-3xl font-black text-white">{target.brand}</h3>
                    <p className="text-sm text-white/60 mt-1">{target.reason}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-white/10">
                  <div>
                    <span className="text-xs text-white/40 uppercase tracking-wider">Since</span>
                    <p className="text-sm text-white font-medium mt-0.5">{target.since}</p>
                  </div>
                  <div>
                    <span className="text-xs text-white/40 uppercase tracking-wider">Competitor</span>
                    <p className="text-sm text-white font-medium mt-0.5">{target.competitor || "—"}</p>
                  </div>
                  <div>
                    <span className="text-xs text-white/40 uppercase tracking-wider">Tweet Text</span>
                    <p className="text-xs text-white/60 mt-0.5 line-clamp-2">{target.tweet_text || "—"}</p>
                  </div>
                </div>
              </div>
            ) : !editingTarget ? (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
                <Icon name="ExclamationTriangleIcon" size={32} variant="outline" className="text-white/20 mx-auto mb-3" />
                <p className="text-white/40 text-sm">No active target set. Click "Set Target" to add one.</p>
              </div>
            ) : null}

            {editingTarget && (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <h3 className="text-sm font-semibold text-white/80 mb-4">
                  {target ? "Edit Current Target" : "Set New Target"}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: "brand", label: "Brand Name", placeholder: "e.g. StreamFlix Premium" },
                    { key: "since", label: "Since (YYYY-MM-DD)", placeholder: "2026-02-10" },
                    { key: "competitor", label: "Competitor (optional)", placeholder: "e.g. ClearStream" },
                    { key: "ad_type", label: "", placeholder: "" },
                  ]
                    .filter((f) => f.key !== "ad_type")
                    .map((field) => (
                      <div key={field.key}>
                        <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wider">
                          {field.label}
                        </label>
                        <input
                          type="text"
                          value={targetForm[field.key as keyof typeof targetForm]}
                          onChange={(e) =>
                            setTargetForm((p) => ({ ...p, [field.key]: e.target.value }))
                          }
                          placeholder={field.placeholder}
                          className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#FF2222] transition-colors"
                        />
                      </div>
                    ))}
                  <div className="md:col-span-2">
                    <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wider">
                      Reason
                    </label>
                    <input
                      type="text"
                      value={targetForm.reason}
                      onChange={(e) => setTargetForm((p) => ({ ...p, reason: e.target.value }))}
                      placeholder="Why is this brand being targeted?"
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#FF2222] transition-colors"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wider">
                      Tweet Text
                    </label>
                    <textarea
                      value={targetForm.tweet_text}
                      onChange={(e) => setTargetForm((p) => ({ ...p, tweet_text: e.target.value }))}
                      placeholder="Pre-written tweet for the community..."
                      rows={3}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#FF2222] transition-colors resize-none"
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-5">
                  <button
                    onClick={handleSaveTarget}
                    disabled={loading}
                    className="flex items-center gap-2 bg-[#FF2222] text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#CC0000] transition-colors disabled:opacity-60"
                  >
                    {loading ? "Saving…" : "Save Target"}
                  </button>
                  <button
                    onClick={() => setEditingTarget(false)}
                    className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white/50 hover:text-white border border-white/10 hover:border-white/30 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── BLACKLIST ── */}
        {activeTab === "blacklist" && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <h2 className="font-display text-2xl font-black text-white">Advertiser Blacklist</h2>
              <div className="flex flex-wrap gap-2">
                {(["all", "pending", "approved", "rejected"] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setBlacklistFilter(f)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors ${
                      blacklistFilter === f
                        ? "bg-[#FF2222] text-white"
                        : "bg-white/5 text-white/50 hover:text-white border border-white/10"
                    }`}
                  >
                    {f}
                    {f !== "all" && (
                      <span className="ml-1 opacity-60">
                        ({blacklist.filter((e) => e.blacklist_status === f).length})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <input
                type="text"
                value={blacklistSearch}
                onChange={(e) => setBlacklistSearch(e.target.value)}
                placeholder="Search brand or industry…"
                className="w-full max-w-sm bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#FF2222] transition-colors"
              />
            </div>

            {/* Edit modal */}
            {editingBlacklist && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
                <div className="bg-[#1C1C1C] border border-white/10 rounded-2xl p-6 w-full max-w-lg">
                  <h3 className="font-display text-xl font-black text-white mb-4">Edit Entry</h3>
                  <div className="space-y-3">
                    {[
                      { key: "brand", label: "Brand" },
                      { key: "industry", label: "Industry" },
                      { key: "ad_type", label: "Ad Type" },
                    ].map((f) => (
                      <div key={f.key}>
                        <label className="block text-xs text-white/50 mb-1 uppercase tracking-wider">
                          {f.label}
                        </label>
                        <input
                          type="text"
                          value={editingBlacklist[f.key as keyof BlacklistEntry] as string}
                          onChange={(e) =>
                            setEditingBlacklist((p) => p ? { ...p, [f.key]: e.target.value } : p)
                          }
                          className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#FF2222] transition-colors"
                        />
                      </div>
                    ))}
                    <div>
                      <label className="block text-xs text-white/50 mb-1 uppercase tracking-wider">Status</label>
                      <select
                        value={editingBlacklist.blacklist_status}
                        onChange={(e) =>
                          setEditingBlacklist((p) =>
                            p ? { ...p, blacklist_status: e.target.value as BlacklistEntry["blacklist_status"] } : p
                          )
                        }
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#FF2222] transition-colors"
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-white/50 mb-1 uppercase tracking-wider">Notes</label>
                      <textarea
                        value={editingBlacklist.notes ?? ""}
                        onChange={(e) =>
                          setEditingBlacklist((p) => p ? { ...p, notes: e.target.value } : p)
                        }
                        rows={2}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#FF2222] transition-colors resize-none"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-5">
                    <button
                      onClick={handleSaveBlacklist}
                      disabled={loading}
                      className="flex-1 bg-[#FF2222] text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-[#CC0000] transition-colors disabled:opacity-60"
                    >
                      {loading ? "Saving…" : "Save Changes"}
                    </button>
                    <button
                      onClick={() => setEditingBlacklist(null)}
                      className="flex-1 border border-white/10 text-white/50 py-2.5 rounded-xl text-sm font-semibold hover:text-white hover:border-white/30 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {filteredBlacklist.length === 0 ? (
                <div className="text-center py-12 text-white/30 text-sm">No entries found.</div>
              ) : (
                filteredBlacklist.map((entry) => (
                  <div
                    key={entry.id}
                    className="bg-white/5 border border-white/10 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-white text-sm">{entry.brand}</span>
                        <StatusPill value={entry.blacklist_status} map={blacklistStatusMap} />
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/40">
                        <span>{entry.industry}</span>
                        <span>{entry.ad_type}</span>
                        <span>{entry.streaming_services?.join(", ")}</span>
                        <span>{entry.report_count.toLocaleString()} reports</span>
                        <span>↑{entry.upvotes} ↓{entry.downvotes}</span>
                        <span>{formatDate(entry.created_at)}</span>
                      </div>
                      {entry.notes && (
                        <p className="text-xs text-white/30 mt-1 italic line-clamp-1">{entry.notes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {entry.blacklist_status === "pending" && (
                        <>
                          <button
                            onClick={() => handleBlacklistStatus(entry.id, "approved")}
                            className="flex items-center gap-1 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-emerald-500/30 transition-colors"
                          >
                            <Icon name="CheckIcon" size={12} variant="solid" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleBlacklistStatus(entry.id, "rejected")}
                            className="flex items-center gap-1 bg-red-500/20 text-red-400 border border-red-500/30 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-red-500/30 transition-colors"
                          >
                            <Icon name="XMarkIcon" size={12} variant="solid" />
                            Reject
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => setEditingBlacklist(entry)}
                        className="p-1.5 rounded-lg bg-white/5 text-white/40 hover:text-white hover:bg-white/10 transition-colors border border-white/10"
                        aria-label="Edit entry"
                      >
                        <Icon name="PencilSquareIcon" size={14} variant="outline" />
                      </button>
                      <button
                        onClick={() => handleDeleteBlacklist(entry.id)}
                        className="p-1.5 rounded-lg bg-white/5 text-white/40 hover:text-[#FF2222] hover:bg-red-500/10 transition-colors border border-white/10"
                        aria-label="Delete entry"
                      >
                        <Icon name="TrashIcon" size={14} variant="outline" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ── REPORTS ── */}
        {activeTab === "reports" && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <h2 className="font-display text-2xl font-black text-white">Community Reports</h2>
              <div className="flex flex-wrap gap-2">
                {(["all", "open", "reviewed", "resolved", "dismissed"] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setReportFilter(f)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors ${
                      reportFilter === f
                        ? "bg-[#FF2222] text-white"
                        : "bg-white/5 text-white/50 hover:text-white border border-white/10"
                    }`}
                  >
                    {f}
                    {f !== "all" && (
                      <span className="ml-1 opacity-60">
                        ({reports.filter((r) => r.report_status === f).length})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              {filteredReports.length === 0 ? (
                <div className="text-center py-12 text-white/30 text-sm">No reports found.</div>
              ) : (
                filteredReports.map((report) => (
                  <div
                    key={report.id}
                    className="bg-white/5 border border-white/10 rounded-xl p-4 flex flex-col sm:flex-row sm:items-start gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-white text-sm">{report.brand}</span>
                        <StatusPill value={report.report_status} map={reportStatusMap} />
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/40 mb-1">
                        <span>{report.streaming_service}</span>
                        <span>{report.industry}</span>
                        <span>{report.ad_type}</span>
                        {report.reporter_email && <span>{report.reporter_email}</span>}
                        <span>{formatDate(report.created_at)}</span>
                      </div>
                      {report.notes && (
                        <p className="text-xs text-white/50 italic mt-1">&ldquo;{report.notes}&rdquo;</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                      {(["open", "reviewed", "resolved", "dismissed"] as const)
                        .filter((s) => s !== report.report_status)
                        .slice(0, 2)
                        .map((s) => (
                          <button
                            key={s}
                            onClick={() => handleReportStatus(report.id, s)}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold capitalize bg-white/5 text-white/50 hover:text-white border border-white/10 hover:border-white/30 transition-colors"
                          >
                            → {s}
                          </button>
                        ))}
                      <button
                        onClick={() => handleDeleteReport(report.id)}
                        className="p-1.5 rounded-lg bg-white/5 text-white/40 hover:text-[#FF2222] hover:bg-red-500/10 transition-colors border border-white/10"
                        aria-label="Delete report"
                      >
                        <Icon name="TrashIcon" size={14} variant="outline" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ── SUBSCRIBERS ── */}
        {activeTab === "subscribers" && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <h2 className="font-display text-2xl font-black text-white">Newsletter Subscribers</h2>
              <div className="flex gap-2">
                {(["all", "active", "inactive"] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setSubscriberFilter(f)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors ${
                      subscriberFilter === f
                        ? "bg-[#FF2222] text-white"
                        : "bg-white/5 text-white/50 hover:text-white border border-white/10"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              {filteredSubscribers.length === 0 ? (
                <div className="text-center py-12 text-white/30 text-sm">No subscribers found.</div>
              ) : (
                filteredSubscribers.map((sub) => (
                  <div
                    key={sub.id}
                    className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 flex items-center gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-white font-medium truncate">{sub.email}</span>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                            sub.is_active
                              ? "bg-emerald-500/20 text-emerald-400" :"bg-gray-500/20 text-gray-400"
                          }`}
                        >
                          {sub.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <p className="text-xs text-white/30 mt-0.5">
                        Subscribed {formatDate(sub.subscribed_at)}
                        {sub.unsubscribed_at && ` · Unsubscribed ${formatDate(sub.unsubscribed_at)}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleToggleSubscriber(sub.id, sub.is_active)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${
                          sub.is_active
                            ? "bg-white/5 text-white/50 border-white/10 hover:text-white hover:border-white/30" :"bg-emerald-500/20 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/30"
                        }`}
                      >
                        {sub.is_active ? "Deactivate" : "Reactivate"}
                      </button>
                      <button
                        onClick={() => handleDeleteSubscriber(sub.id)}
                        className="p-1.5 rounded-lg bg-white/5 text-white/40 hover:text-[#FF2222] hover:bg-red-500/10 transition-colors border border-white/10"
                        aria-label="Remove subscriber"
                      >
                        <Icon name="TrashIcon" size={14} variant="outline" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
